import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';

import ResponsiveModalPage from '@/components/ResponsiveModalPage';
import { toast } from '@/components/toastStore';
import { useInfiniteScrollSentinel } from '@/hooks/useInfiniteScrollSentinel';
import {
  PackageType,
  type BookingWithPackageUsage,
  type CustomerPackage,
} from '@/models';
import type { PagedResponse } from '@/models/profile';
import {
  CashWalletBadge,
  MinutesBadge,
  PercentBadge,
  TimesBadge,
} from '@/pages/components/Badge';
import ReorderBottomSheet from '@/pages/components/ReorderBottomSheet';
import BookingHistoryItem from '@/pages/profile/components/BookingHistoryItem';
import BookingHistorySkeletonList from '@/pages/profile/components/BookingHistorySkeletonList';
import { ProfileService } from '@/services';

interface PackageDetailsPageProps {
  isOpen: boolean;
  onClose: () => void;
  packageId: number | null;
}

const PackageDetailsPage: FC<PackageDetailsPageProps> = ({
  isOpen,
  onClose,
  packageId,
}) => {
  const { t } = useTranslation();
  const [reorderBooking, setReorderBooking] =
    useState<BookingWithPackageUsage | null>(null);

  const packageQuery = useQuery({
    queryKey: ['profile', 'customerPackage', packageId],
    enabled: isOpen && packageId !== null,
    queryFn: async (): Promise<CustomerPackage | null> => {
      if (packageId === null) return null;

      return await ProfileService.getCustomerPackageById(packageId);
    },
  });

  const customerPackage = packageQuery.data;

  const usageQuery = useInfiniteQuery({
    queryKey: ['profile', 'customerPackageUsage', packageId],
    enabled: isOpen && packageId !== null,
    initialPageParam: 1,
    queryFn: async ({
      pageParam,
    }): Promise<PagedResponse<BookingWithPackageUsage>> => {
      if (packageId === null) {
        return { items: [], totalPage: 1 };
      }

      return await ProfileService.getCustomerPackageUsage(packageId, {
        limit: 10,
        page: Number(pageParam),
      });
    },
    getNextPageParam: (lastPage, allPages) => {
      const nextPage = allPages.length + 1;
      if (nextPage > lastPage.totalPage) return undefined;
      return nextPage;
    },
  });

  const usageBookings = useMemo((): BookingWithPackageUsage[] => {
    if (!usageQuery.data) return [];
    return usageQuery.data.pages.flatMap((p) => p.items);
  }, [usageQuery.data]);

  const {
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading: usageLoading,
  } = usageQuery;

  useEffect(() => {
    if (!usageQuery.isError) return;
    console.error('Failed to load package usage', usageQuery.error);
    toast.error(t('package.detailsPage.errors.loadUsageFailed'));
  }, [t, usageQuery.error, usageQuery.errorUpdatedAt, usageQuery.isError]);

  const usageSentinelRef = useInfiniteScrollSentinel({
    enabled: isOpen && packageId !== null,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
  });

  const badge = useMemo(() => {
    if (!customerPackage) return null;
    switch (customerPackage.type) {
      case PackageType.CashWallet:
        return <CashWalletBadge />;
      case PackageType.Times:
        return <TimesBadge />;
      case PackageType.Minutes:
        return <MinutesBadge />;
      case PackageType.Percent:
        return <PercentBadge />;
      default:
        return null;
    }
  }, [customerPackage]);

  const formatValue = (value: number, kind: 'amount' | 'remaining'): string => {
    const base = value.toLocaleString();

    if (!customerPackage) return base;

    switch (customerPackage.type) {
      case PackageType.CashWallet:
        return base;
      case PackageType.Times:
        return `${base} ${t('package.unit.times')}`;
      case PackageType.Minutes:
        return base;
      case PackageType.Percent:
        return kind === 'amount' ? `${base}%` : base;
      default:
        return base;
    }
  };

  const purchaseDateLabel = useMemo(() => {
    if (!customerPackage) return '—';

    const enriched = customerPackage as CustomerPackage & {
      purchaseDate?: string | Date;
      createdAt?: string | Date;
    };

    const raw = enriched.purchaseDate ?? enriched.createdAt;

    if (typeof raw === 'string' || raw instanceof Date) {
      const d = dayjs(raw);
      if (d.isValid()) return d.format('DD/MM/YYYY');
    }

    return '—';
  }, [customerPackage]);

  const expiryDateLabel = useMemo(() => {
    if (!customerPackage) return '—';
    const d = dayjs(customerPackage.expiryDate);
    return d.isValid() ? d.format('DD/MM/YYYY') : '—';
  }, [customerPackage]);

  const expiringSoonDaysLabel = useMemo(() => {
    if (!customerPackage) return null;

    const thresholdDaysRaw = import.meta.env.VITE_PACKAGE_EXPIRY_WARNING_DAYS;
    const thresholdDays = Number(thresholdDaysRaw);
    const warningDays = Number.isFinite(thresholdDays) ? thresholdDays : 30;

    const expiry = dayjs(customerPackage.expiryDate).startOf('day');
    const today = dayjs().startOf('day');
    if (!expiry.isValid()) return null;

    const daysLeft = expiry.diff(today, 'day');
    if (daysLeft <= 0) return null;
    if (daysLeft > warningDays) return null;

    return t('package.detailsPage.expiryWarningDays', { count: daysLeft });
  }, [customerPackage, t]);

  return (
    <ResponsiveModalPage
      isOpen={isOpen}
      onClose={onClose}
      title={t('package.details')}
      desktopWidthClass="md:w-[520px]"
      desktopHeightClass="md:h-[85vh]"
    >
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pb-20">
        <div className="flex flex-col gap-2 pt-2">
          <div className="flex flex-col gap-0.5">
            <p className="text-secondary-text text-sm leading-5">
              {t('package.detailsPage.fields.package')}
            </p>

            {customerPackage ? (
              <div className="flex flex-col gap-0.5">
                <p className="text-primary-text text-base leading-6">
                  {customerPackage.name}
                </p>
                {badge}
              </div>
            ) : packageId !== null && packageQuery.isLoading ? (
              <div className="space-y-2" aria-hidden="true">
                <div className="bg-divider h-6 w-40 animate-pulse rounded" />
                <div className="bg-divider h-6 w-20 animate-pulse rounded" />
              </div>
            ) : (
              <p className="text-secondary-text text-sm leading-5">
                {t('package.detailsPage.errors.packageNotFound', {
                  id: packageId ?? '—',
                })}
              </p>
            )}
          </div>

          <div className="flex w-full gap-4">
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <p className="text-secondary-text text-sm leading-5">
                {t('package.detailsPage.fields.amount')}
              </p>
              <p className="text-primary-text text-base leading-6">
                {customerPackage
                  ? formatValue(customerPackage.totalValue, 'amount')
                  : '—'}
              </p>
            </div>

            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <p className="text-secondary-text text-sm leading-5">
                {t('package.detailsPage.fields.remainingAmount')}
              </p>
              <p className="text-primary-text text-base leading-6">
                {customerPackage
                  ? formatValue(customerPackage.remainingValue, 'remaining')
                  : '—'}
              </p>
            </div>
          </div>

          <div className="flex w-full gap-4">
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <p className="text-secondary-text text-sm leading-5">
                {t('package.detailsPage.fields.purchaseDate')}
              </p>
              <p className="text-primary-text text-base leading-6">
                {purchaseDateLabel}
              </p>
            </div>

            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <p className="text-secondary-text text-sm leading-5">
                {t('package.detailsPage.fields.expiryDate')}
              </p>
              <div className="flex w-full items-center gap-4">
                <p className="text-primary-text min-w-0 flex-1 text-base leading-6">
                  {expiryDateLabel}
                </p>

                {expiringSoonDaysLabel ? (
                  <div className="bg-warning/10 text-warning inline-flex h-6 shrink-0 items-center justify-center overflow-hidden rounded px-1">
                    <p className="text-center text-sm leading-5 whitespace-nowrap">
                      {expiringSoonDaysLabel}
                    </p>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
        <hr className="border-divider mt-5" />

        <div className="pt-4">
          <div className="pb-3">
            <h2 className="text-primary-text text-base leading-6 font-semibold">
              {t('package.detailsPage.sections.usage')}
            </h2>
          </div>

          {usageLoading && usageBookings.length === 0 ? (
            <BookingHistorySkeletonList />
          ) : usageBookings.length > 0 ? (
            <div className="flex flex-col gap-2">
              {usageBookings.map((booking, index) => (
                <BookingHistoryItem
                  key={`${booking.availabilityId}-${index}`}
                  booking={booking}
                  packageUsageAmount={booking.packageUsageAmount}
                  packageType={customerPackage?.type}
                  onReorderClick={() => {
                    setReorderBooking(booking);
                  }}
                  onClick={() => {
                    // No booking details bottom sheet here yet.
                  }}
                />
              ))}

              <div ref={usageSentinelRef} className="h-0.5" />

              {isFetchingNextPage ? (
                <div className="pt-2" aria-hidden="true">
                  <BookingHistorySkeletonList count={1} />
                </div>
              ) : null}
            </div>
          ) : (
            <p className="text-secondary-text text-sm leading-5">
              {t('package.detailsPage.usage.empty')}
            </p>
          )}
        </div>

        <ReorderBottomSheet
          isOpen={reorderBooking !== null}
          onClose={() => {
            setReorderBooking(null);
          }}
          booking={reorderBooking}
        />
      </div>
    </ResponsiveModalPage>
  );
};

export default PackageDetailsPage;
